import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { getValidated, AppError } from '@shared/api';
import { expenseKeys } from './keys';
import {
  PendingExpenseAttachmentViewResponseSchema,
  TransactionListResponseSchema,
} from './schemas';
import { toLedgerEntry } from './mappers';
import type { LedgerEntry, PendingExpenseAttachment } from '../model/types';

/**
 * A group's ledger from the journal-entry feed `GET /api/v1/expense/{groupId}/new?page=N` — the accounting
 * source of truth: expenses, settlements, wallet deposits and wallet-balance closures, each mapped
 * to a `LedgerEntry` (unknown `reference_type`s are dropped). Page-based pagination (20 items/page).
 */
export function transactionsInfiniteQueryOptions(groupId: string) {
  return infiniteQueryOptions({
    queryKey: expenseKeys.list({ groupId }),
    queryFn: async ({ pageParam }) => {
      try {
        const res = await getValidated(
          `api/v1/expense/${groupId}/new`,
          TransactionListResponseSchema,
          { page: pageParam },
        );
        return res.data.transactions;
      } catch (error) {
        if (error instanceof AppError && error.status === 404) return [];
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < 20) return undefined;
      return allPages.length + 1;
    },
    select: (data) => {
      const seen = new Set<string>();
      const deduplicated = [];
      for (const page of data.pages) {
        for (const tx of page) {
          const key =
            tx.record_id ??
            tx.expense?._id ??
            tx.settle?._id ??
            tx.deposit?.record_id ??
            tx.wallet_balance_closure?.record_id ??
            JSON.stringify(tx);
          if (!seen.has(key)) {
            seen.add(key);
            deduplicated.push(tx);
          }
        }
      }
      return deduplicated
        .map(toLedgerEntry)
        .filter((entry): entry is LedgerEntry => entry !== null);
    },
  });
}

export function useTransactions(groupId: string) {
  return useInfiniteQuery(transactionsInfiniteQueryOptions(groupId));
}

/**
 * EVERY page of the group ledger feed, in the SERVER's own order (date desc, journal created_at
 * desc) — the reference data the CSV export uses to restore the web display order client-side.
 * Pages are fetched sequentially until a short page; capped defensively. Deduplication mirrors
 * the infinite query's select above.
 */
export async function fetchAllLedgerEntries(groupId: string): Promise<LedgerEntry[]> {
  const seen = new Set<string>();
  const entries: LedgerEntry[] = [];
  const MAX_PAGES = 500;
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    let transactions;
    try {
      const res = await getValidated(
        `api/v1/expense/${groupId}/new`,
        TransactionListResponseSchema,
        { page },
      );
      transactions = res.data.transactions;
    } catch (error) {
      if (error instanceof AppError && error.status === 404) break;
      throw error;
    }
    for (const tx of transactions) {
      const key =
        tx.record_id ??
        tx.expense?._id ??
        tx.settle?._id ??
        tx.deposit?.record_id ??
        tx.wallet_balance_closure?.record_id ??
        JSON.stringify(tx);
      if (seen.has(key)) continue;
      seen.add(key);
      const entry = toLedgerEntry(tx);
      if (entry) entries.push(entry);
    }
    if (transactions.length < 20) break;
  }
  return entries;
}

/**
 * A pending expense's receipt attachment binary (API 5): `GET
 * /api/v1/expense/pending/{expenseId}/attachment` → `{ base64, content_type, size }`. Returns null
 * when there are no bytes ("Attachment not found"). Only runs when enabled + an id is present.
 */
export function pendingExpenseAttachmentQueryOptions(expenseId: string, enabled: boolean) {
  return queryOptions({
    queryKey: expenseKeys.attachment(expenseId),
    queryFn: async (): Promise<PendingExpenseAttachment | null> => {
      const res = await getValidated(
        `api/v1/expense/pending/${expenseId}/attachment`,
        PendingExpenseAttachmentViewResponseSchema,
      );
      const data = res.data;
      if (!data?.base64) return null;
      return { base64: data.base64, contentType: data.content_type ?? '', size: data.size ?? 0 };
    },
    enabled: enabled && Boolean(expenseId),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePendingExpenseAttachment(expenseId: string, enabled: boolean) {
  return useQuery(pendingExpenseAttachmentQueryOptions(expenseId, enabled));
}

/**
 * A committed GROUP/recurring expense's receipt attachment binary: `GET
 * /api/v1/expense/{groupId}/{expenseId}/attachment` → `{ base64, content_type, size }`. Same shape +
 * contract as the pending endpoint, but keyed on the GROUP + expense id (no `pending` segment) — group-
 * ledger expenses live in the group-expense collection, so their receipt is served here.
 */
export function groupExpenseAttachmentQueryOptions(
  groupId: string,
  expenseId: string,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: expenseKeys.groupAttachment(expenseId),
    queryFn: async (): Promise<PendingExpenseAttachment | null> => {
      const res = await getValidated(
        `api/v1/expense/${groupId}/${expenseId}/attachment`,
        PendingExpenseAttachmentViewResponseSchema,
      );
      const data = res.data;
      if (!data?.base64) return null;
      return { base64: data.base64, contentType: data.content_type ?? '', size: data.size ?? 0 };
    },
    enabled: enabled && Boolean(groupId) && Boolean(expenseId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGroupExpenseAttachment(groupId: string, expenseId: string, enabled: boolean) {
  return useQuery(groupExpenseAttachmentQueryOptions(groupId, expenseId, enabled));
}
